import type { TrackingDebugService } from '../services/TrackingDebugService';
import type { ConsentStrategy } from './ConsentStrategy';
import { ConsentmanagerAppStrategy } from './ConsentmanagerAppStrategy';
import { ConsentmanagerNetStrategy } from './ConsentmanagerNetStrategy';
import { CookiebotStrategy } from './CookiebotStrategy';
import { OverrideStrategy } from './OverrideStrategy';
import { ShopwareNativeStrategy } from './ShopwareNativeStrategy';
import { UsercentricsStrategy } from './UsercentricsStrategy';

/** Reported when nothing could decide — e.g. every probe threw. Fail closed. */
const NO_SOURCE = 'none';

/**
 * Priority order: the first *present* mechanism decides and everything behind it is ignored,
 * so a shop running a CMP is never second-guessed by a stale cookie. `ShopwareNativeStrategy`
 * is always present, which terminates the list.
 *
 * Adding a CMP is a one-class change: implement `ConsentStrategy`, insert it here.
 */
export function createDefaultConsentStrategies(): ConsentStrategy[] {
  return [
    new OverrideStrategy(),
    new CookiebotStrategy(),
    new UsercentricsStrategy(),
    new ConsentmanagerNetStrategy(),
    new ConsentmanagerAppStrategy(),
    new ShopwareNativeStrategy(),
  ];
}

/**
 * A third-party CMP that throws must not break the poll loop, and must not shadow the
 * strategies behind it either — so both probes are guarded and a throwing strategy counts as
 * absent for this tick. It will be retried on the next one.
 */
export function resolveConsent(
  strategies: ConsentStrategy[],
  debug?: Pick<TrackingDebugService, 'log'>,
): { granted: boolean; source: string } {
  for (const strategy of strategies) {
    try {
      if (!strategy.isPresent()) {
        continue;
      }

      return { granted: strategy.hasConsent(), source: strategy.name };
    } catch (error) {
      debug?.log('resolveConsent', 'consent strategy probe failed', {
        strategy: strategy.name,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return { granted: false, source: NO_SOURCE };
}
